<template>
  <nuxt-link
    class="content"
    :class="{
      completed,
      'next-completed': nextCompleted,
      'next-not-completed': !nextCompleted,
      'previous-completed': previousCompleted,
      'previous-not-completed': !previousCompleted,
      first: index === 0,
      last: index === length - 1,
    }"
    :to="path"
    @click.capture="handleClickPath"
  >
    <span class="progress">
      <span class="graphic" />
    </span>
    <span class="label">
      <span class="text">{{ text }}</span>
      <Icon
        v-if="isTrialItem"
        class="icon-gift"
        size="2rem"
        name="fluent-emoji-flat:wrapped-gift"
      />
    </span>

    <span class="mark-out"> {{ scoreOutOf20 }} / 20 </span>
  </nuxt-link>
</template>
<script lang="ts" setup>
interface Props {
  index: number;
  libraryBookId: string;
  id: string;
  lockedContent: string;
  nbItemsCompleted: number;
  length: number;
  path: string;
  text: string;
  score: number;
  isStars: boolean;
  previousCompleted: boolean;
  nextCompleted: boolean;
  completed: boolean;
  locked?: boolean;
  availableByOrder?: boolean;
  isParentChildPage?: boolean;
}
const props = defineProps({
  index: null,
  libraryBookId: null,
  id: null,
  lockedContent: null,
  nbItemsCompleted: null,
  length: null,
  path: null,
  text: null,
  score: null,
  isStars: { type: Boolean },
  previousCompleted: { type: Boolean },
  nextCompleted: { type: Boolean },
  completed: { type: Boolean },
  locked: { type: Boolean, default: false },
  availableByOrder: { type: Boolean, default: true },
  isParentChildPage: { type: Boolean }
});

const { progressions } = inject(INJECT_PROGRESSION_EXERCISES);

const exerciseProgressionInfo = useExerciseProgressionInfo(
  progressions,
  props.id
);

const { scoreOutOf20 } = exerciseProgressionInfo;

const { handleClickPath, isTrialItem } = useCategoryContentClick(
  {
    lockedContent: props.lockedContent,
    id: props.id,
  },
  props.path
);
</script>
<style lang="scss" scoped>
.content {
  display: flex;
  position: relative;

  // grid-template-columns: 2.6rem 1fr 10rem;
  align-items: center;
  height: auto;
  gap: var(--gap_n);

  // Fix Safari issue if before and after are inside progress element
  &::before,
  &::after {
    content: '';
    display: block;
    position: absolute;
    top: 0%;
    left: 1.3rem;
    width: 4px;
    height: 50%;
    margin-left: -0.2rem;
    background-color: var(--color_neutral_100);
  }
  &::after {
    top: 50%;
  }

  &.completed {
    &.previous-completed {
      &::before {
        background: var(--color_secondary);
      }
    }

    &.previous-not-completed {
      &::before {
        background: linear-gradient(
          0deg,
          var(--color_secondary) 0%,
          var(--color_neutral_100) 100%
        );
      }
    }
    &.next-completed {
      &::after {
        background: var(--color_secondary);
      }
    }
    &.next-not-completed {
      &::after {
        background: linear-gradient(
          0deg,
          var(--color_neutral_100) 0%,
          var(--color_secondary) 100%
        );
      }
    }
  }

  &.first {
    &::before {
      display: none;
    }
  }
  &.last {
    &::after {
      display: none;
    }
  }
}
.progress {
  display: flex;
  position: relative;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 2.6rem;
  .graphic {
    position: absolute;
    z-index: 1;
    width: 1.6rem;
    height: 1.6rem;
    border-radius: 50%;
    background: var(--color_neutral_100);
  }
}
.completed {
  .progress {
    .graphic {
      background: var(--color_secondary);
    }
  }
}

.label {
  position: relative;
  flex-grow: 3;
  padding: 1rem 0;
  transition: color 300ms $ease-out-quad;
  color: var(--color_neutral_500);
  text-align: left;
  @include font('n', '500');
  .icon-gift {
    margin-left: 0.6rem;
  }
  .text {
    position: relative;
    &::before {
      content: '';
      position: absolute;
      z-index: 0;
      bottom: -0.5rem;
      left: 0;
      width: 100%;
      height: 0.5rem;
      transform: scaleX(0);
      transform-origin: 0%;
      transition: transform 300ms $ease-out-quad;
      background: var(--color_secondary);
    }
  }
}
a {
  &:hover,
  &:focus,
  &.active,
  &.disabled,
  &.router-link-active,
  &.router-link-exact-active {
    .label {
      .text {
        color: var(--color_secondary);
        &::before {
          transform: scale(1);
        }
      }
    }
  }
}

.mark-out {
  @include font('m', '800');

  flex-shrink: 0;
  color: var(--color_secondary);
  font-style: italic;
  text-align: right;
}
.stars {
  flex-shrink: 0;
}
</style>
